import React, { useState, useEffect } from 'react';
import { 
    Modal, Form, Button, Alert, 
    Badge, Card, ProgressBar,
    ButtonGroup, InputGroup
} from 'react-bootstrap';
import { 
    FaFolder, FaFileAlt, FaArrowRight, FaCheck, 
    FaTimes, FaSearch, FaArrowsAlt, FaSync
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import dynamicFormsService from '../../../services/dynamicFormsService';

const FormAssignmentWizard = ({ 
    show, 
    onHide, 
    folders, 
    forms, 
    onFormsAssigned 
}) => {
    const [selectedForms, setSelectedForms] = useState([]);
    const [targetFolder, setTargetFolder] = useState('');
    const [operation, setOperation] = useState('move'); // 'move' or 'remove'
    const [searchTerm, setSearchTerm] = useState('');
    const [processing, setProcessing] = useState(false);
    const [step, setStep] = useState(1); // 1: Select Forms, 2: Choose Destination, 3: Confirm
    
    // Reset state when modal opens/closes
    useEffect(() => {
        if (show) {
            setSelectedForms([]);
            setTargetFolder('');
            setOperation('move');
            setSearchTerm('');
            setStep(1);
        }
    }, [show]);

    const getFilteredForms = () => {
        return forms.filter(form =>
            form.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            form.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const getFormCountByFolder = (folderId) => {
        return forms.filter(form => form.folder_id === folderId).length;
    };



    const getFolderName = (folderId) => {
        const folder = folders.find(f => f.folder_id === folderId);
        return folder ? folder.name : 'Unknown Folder';
    };

    const handleFormSelection = (formId) => {
        setSelectedForms(prev => {
            if (prev.includes(formId)) {
                return prev.filter(id => id !== formId);
            } else {
                return [...prev, formId];
            }
        });
    };

    const handleSelectAll = () => {
        const filteredFormIds = getFilteredForms().map(form => 
            form.form_id || form.id || form._id
        ).filter(id => id);
        setSelectedForms(filteredFormIds);
    };

    const handleClearAll = () => {
        setSelectedForms([]);
    };

    const handleProcessForms = async () => {
        if (selectedForms.length === 0) {
            toast.error('Please select at least one form');
            return;
        }

        if (operation === 'move' && !targetFolder) {
            toast.error('Please select a target folder');
            return;
        }

        setProcessing(true);
        try {
            let response;
            
            if (operation === 'move') {
                response = await dynamicFormsService.moveFormsToFolder(targetFolder, selectedForms);
            } else {
                response = await dynamicFormsService.removeFormsFromFolders(selectedForms);
            }

            if (response.success) {
                const action = operation === 'move' ? 'moved to folder' : 'removed from folders';
                toast.success(`${selectedForms.length} forms ${action} successfully`);
                onFormsAssigned(); // Refresh parent data
                onHide();
            } else {
                toast.error(response.message || `Failed to ${operation} forms`);
            }
        } catch (error) {
            console.error('Error processing forms:', error);
            toast.error(`Error ${operation === 'move' ? 'moving' : 'removing'} forms`);
        } finally {
            setProcessing(false);
        }
    };

    const renderFormSelection = () => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Select Forms to {operation === 'move' ? 'Move' : 'Remove'}</h6>
                <div>
                    <ButtonGroup size="sm">
                        <Button 
                            variant={operation === 'move' ? 'primary' : 'outline-primary'}
                            onClick={() => setOperation('move')}
                        >
                            <FaArrowsAlt className="me-1" />
                            Move to Folder
                        </Button>
                        <Button 
                            variant={operation === 'remove' ? 'primary' : 'outline-primary'}
                            onClick={() => setOperation('remove')}
                        >
                            <FaTimes className="me-1" />
                            Remove from Folders
                        </Button>
                    </ButtonGroup>
                </div>
            </div>

            <InputGroup className="mb-3">
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                    type="text"
                    placeholder="Search forms..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-muted">
                    {getFilteredForms().length} forms available
                </small>
                <div>
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={handleSelectAll}
                        className="me-2"
                    >
                        Select All
                    </Button>
                    <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={handleClearAll}
                    >
                        Clear All
                    </Button>
                </div>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {getFilteredForms().map(form => {
                    const formId = form.form_id || form.id || form._id;
                    if (!formId) return null;

                    return (
                        <Card 
                            key={formId} 
                            className={`mb-2 cursor-pointer ${selectedForms.includes(formId) ? 'border-primary' : ''}`}
                            onClick={() => handleFormSelection(formId)}
                        >
                            <Card.Body className="p-3">
                                <div className="d-flex align-items-center">
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectedForms.includes(formId)}
                                        onChange={() => handleFormSelection(formId)}
                                        className="me-3"
                                    />
                                    <FaFileAlt className="text-primary me-3" />
                                    <div className="flex-grow-1">
                                        <h6 className="mb-1">{form.name}</h6>
                                        {form.description && (
                                            <p className="text-muted small mb-1">{form.description}</p>
                                        )}
                                        {form.folder_id && (
                                            <Badge bg="secondary">
                                                Current: {getFolderName(form.folder_id)}
                                            </Badge>
                                        )}
                                        {!form.folder_id && (
                                            <Badge bg="warning">Unassigned</Badge>
                                        )}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    );
                })}
            </div>

            {getFilteredForms().length === 0 && (
                <div className="text-center p-4">
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <p className="text-muted">No forms found matching your search.</p>
                </div>
            )}
        </div>
    );

    const renderFolderSelection = () => (
        <div>
            <h6 className="mb-3">Choose Target Folder</h6>
            <p className="text-muted mb-3">
                Select the folder where you want to move the {selectedForms.length} selected forms.
            </p>

            <Form.Group>
                <Form.Label>Target Folder</Form.Label>
                <Form.Select
                    value={targetFolder}
                    onChange={(e) => setTargetFolder(e.target.value)}
                    required
                >
                    <option value="">Select a folder...</option>
                    {folders.map(folder => (
                        <option key={folder.folder_id} value={folder.folder_id}>
                            {folder.name} ({getFormCountByFolder(folder.folder_id)} forms)
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            <div className="mt-3">
                <h6 className="small">Folder Overview:</h6>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {folders.map(folder => (
                        <Card 
                            key={folder.folder_id} 
                            className={`mb-2 cursor-pointer ${targetFolder === folder.folder_id ? 'border-primary bg-light' : ''}`}
                            onClick={() => setTargetFolder(folder.folder_id)}
                        >
                            <Card.Body className="p-2">
                                <div className="d-flex align-items-center">
                                    <FaFolder className="text-primary me-2" />
                                    <div className="flex-grow-1">
                                        <small className="fw-bold">{folder.name}</small>
                                        <div className="d-flex align-items-center gap-2">
                                            <Badge bg="primary" size="sm">
                                                {getFormCountByFolder(folder.folder_id)} forms
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderConfirmation = () => (
        <div>
            <h6 className="mb-3">Confirm Operation</h6>
            
            <Alert variant="info">
                <div className="d-flex align-items-center">
                    <FaCheck className="me-2" />
                    <div>
                        <strong>Operation Summary:</strong>
                        <ul className="mb-0 mt-2">
                            <li>Action: {operation === 'move' ? 'Move forms to folder' : 'Remove forms from folders'}</li>
                            <li>Forms selected: {selectedForms.length}</li>
                            {operation === 'move' && targetFolder && (
                                <li>Target folder: {getFolderName(targetFolder)}</li>
                            )}
                        </ul>
                    </div>
                </div>
            </Alert>

            <h6 className="small mt-3">Selected Forms:</h6>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedForms.map(formId => {
                    const form = forms.find(f => 
                        (f.form_id || f.id || f._id) === formId
                    );
                    if (!form) return null;

                    return (
                        <div key={formId} className="d-flex align-items-center p-2 border-bottom">
                            <FaFileAlt className="text-primary me-2" />
                            <div className="flex-grow-1">
                                <small className="fw-bold">{form.name}</small>
                                {form.folder_id && (
                                    <div className="small text-muted">
                                        Currently in: {getFolderName(form.folder_id)}
                                    </div>
                                )}
                            </div>
                            {operation === 'move' && (
                                <>
                                    <FaArrowRight className="text-muted mx-2" />
                                    <small className="text-primary">
                                        {getFolderName(targetFolder)}
                                    </small>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return renderFormSelection();
            case 2:
                return operation === 'move' ? renderFolderSelection() : renderConfirmation();
            case 3:
                return renderConfirmation();
            default:
                return renderFormSelection();
        }
    };

    const canProceedToNext = () => {
        switch (step) {
            case 1:
                return selectedForms.length > 0;
            case 2:
                return operation === 'remove' || targetFolder;
            default:
                return true;
        }
    };

    const getNextStepLabel = () => {
        if (step === 1 && operation === 'remove') return 'Confirm';
        if (step === 1 && operation === 'move') return 'Choose Folder';
        if (step === 2) return 'Confirm';
        return 'Next';
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                                                <FaArrowsAlt className="me-2" />
                    Form Assignment Wizard
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body>
                <ProgressBar 
                    className="mb-4" 
                    now={(step / (operation === 'move' ? 3 : 2)) * 100} 
                    variant="primary"
                />
                
                {renderStepContent()}
            </Modal.Body>
            
            <Modal.Footer>
                <div className="w-100 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        Step {step} of {operation === 'move' ? 3 : 2}
                    </small>
                    
                    <div>
                        <Button 
                            variant="secondary" 
                            onClick={onHide}
                            disabled={processing}
                            className="me-2"
                        >
                            Cancel
                        </Button>
                        
                        {step > 1 && (
                            <Button 
                                variant="outline-primary" 
                                onClick={() => setStep(step - 1)}
                                disabled={processing}
                                className="me-2"
                            >
                                Back
                            </Button>
                        )}
                        
                        {step < (operation === 'move' ? 3 : 2) ? (
                            <Button 
                                variant="primary" 
                                onClick={() => setStep(step + 1)}
                                disabled={!canProceedToNext() || processing}
                            >
                                {getNextStepLabel()}
                            </Button>
                        ) : (
                            <Button 
                                variant="success" 
                                onClick={handleProcessForms}
                                disabled={!canProceedToNext() || processing}
                            >
                                {processing ? (
                                    <>
                                        <FaSync className="me-1 fa-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <FaCheck className="me-1" />
                                        {operation === 'move' ? 'Move Forms' : 'Remove Forms'}
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default FormAssignmentWizard;